import { ChevronRight, Home, MoreHorizontal } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { usePageRefresh } from '@/contexts/PageRefreshContext';

interface BreadcrumbItem {
  label: string;
  href: string;
}

// Store navigation history in a module-level variable to persist across component re-renders
let navigationHistory: BreadcrumbItem[] = [];

export function Breadcrumb() {
  const location = useLocation();
  const navigate = useNavigate();
  const { triggerRefresh } = usePageRefresh();
  const previousPathRef = useRef<string>('');
  const isNavigatingViaBreadcrumbRef = useRef<boolean>(false);
  const [, forceUpdate] = useState({});

  // Map path segments to readable labels
  const pathLabels: Record<string, string> = {
    dashboard: 'Home',
    assets: 'Assets',
    transactions: 'Transactions',
    additions: 'Additions',
    depreciation: 'Depreciation',
    revaluation: 'Revaluation',
    insights: 'Insights',
    enquiry: 'Asset Transaction History',
    audit: 'Audit Trail',
    checkinout: 'Check-In / Check-Out',
    calendar: 'Check-In / Check-Out Calendar',
    checkout: 'Check Out Asset',
    settings: 'Settings',
    features: 'Feature Settings',
    compliance: 'Compliance Dashboard',
    'compliance/assets': 'Compliance Assets',
    'compliance/regulations': 'Rules',
    administration: 'Administration',
    imports: 'Import Assets',
    locations: 'Locations',
    categories: 'Categories',
    equipment: 'Equipment',
    'disposal-reasons': 'Disposal Reasons',
    'asset-options': 'Asset Options',
    periods: 'Accounting Periods',
    'settings/periods': 'Accounting Periods',
    help: 'Help & Support',
    tasks: 'Work Items',
    audits: 'Audit Dashboard',
    'audits/dashboard': 'Audit Dashboard',
    active: 'Active Audits',
    'audits/active': 'Active Audits',
    scheduled: 'Scheduled Audits',
    'audits/scheduled': 'Scheduled Audits',
    history: 'Audit History',
    'audits/history': 'Audit History',
    exceptions: 'Audit Exceptions',
    'audits/exceptions': 'Audit Exceptions',
    'teams-permissions': 'Team Settings',
    'administration/teams-permissions': 'Team Settings',
  };

  // Get label for current path
  const getCurrentPageLabel = (pathname: string): string => {
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0 || pathname === '/dashboard') {
      return 'Home';
    }
    
    // For nested routes like /compliance/settings, try the full path first
    if (pathSegments.length > 1) {
      const fullPath = pathSegments.join('/');
      if (pathLabels[fullPath]) {
        return pathLabels[fullPath];
      }
      
      // Try the last two segments joined with a hyphen (e.g., "compliance-settings")
      const lastTwoSegments = pathSegments.slice(-2).join('-');
      if (pathLabels[lastTwoSegments]) {
        return pathLabels[lastTwoSegments];
      }
    }
    
    // Fall back to just the last segment
    const lastSegment = pathSegments[pathSegments.length - 1];
    return pathLabels[lastSegment] || lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  };

  // Check if current path is a detail page that should not update breadcrumb
  const isDetailPage = (pathname: string): boolean => {
    // Pattern: /assets/:id (numeric ID)
    if (/^\/assets\/\d+$/.test(pathname)) return true;
    
    // Pattern: /tasks/:id (GUID format: 8-4-4-4-12 hex digits)
    if (/^\/tasks\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pathname)) return true;
    
    // Pattern: /audits/:id (numeric ID, but not /audits/dashboard, /audits/active, etc.)
    if (/^\/audits\/\d+$/.test(pathname)) return true;
    
    // Pattern: /audits/exceptions/:id (numeric ID)
    if (/^\/audits\/exceptions\/\d+$/.test(pathname)) return true;
    
    // Pattern: /asset-audits/:id (numeric ID, but not /asset-audits/create)
    if (/^\/asset-audits\/\d+$/.test(pathname)) return true;
    
    // Pattern: /asset-audits/recurring/:id (numeric ID)
    if (/^\/asset-audits\/recurring\/\d+$/.test(pathname)) return true;
    
    // Pattern: /administration/locations/:id (numeric ID or 'new')
    if (/^\/administration\/locations\/(\d+|new)$/.test(pathname)) return true;
    
    // Pattern: /administration/equipment/:id (numeric ID or 'new')
    if (/^\/administration\/equipment\/(\d+|new)$/.test(pathname)) return true;
    
    // Pattern: /administration/categories/:id (numeric ID or 'new')
    if (/^\/administration\/categories\/(\d+|new)$/.test(pathname)) return true;
    
    return false;
  };

  // Update navigation history when location changes
  useEffect(() => {
    const currentPath = location.pathname;
    
    // If navigating via breadcrumb, we've already updated the history
    if (isNavigatingViaBreadcrumbRef.current) {
      isNavigatingViaBreadcrumbRef.current = false;
      previousPathRef.current = currentPath;
      return;
    }

    // Skip detail pages - they should not update the breadcrumb
    if (isDetailPage(currentPath)) {
      previousPathRef.current = currentPath;
      return;
    }

    // Check if this path is already the last item in history
    const isAlreadyLastItem = navigationHistory.length > 0 && 
                              navigationHistory[navigationHistory.length - 1].href === currentPath;
    
    // Only skip if we're already on this page AND it's already the last item
    // This ensures we update when navigating to a new page
    if (previousPathRef.current === currentPath && isAlreadyLastItem) {
      return;
    }

    // Add current page to history
    const currentLabel = getCurrentPageLabel(currentPath);
    const newItem: BreadcrumbItem = {
      label: currentLabel,
      href: currentPath,
    };

    // Remove duplicate if it exists elsewhere in history
    navigationHistory = navigationHistory.filter(item => item.href !== currentPath);
    
    // Add to history (keep full history)
    navigationHistory.push(newItem);

    previousPathRef.current = currentPath;
    
    // Force re-render to show updated breadcrumb
    forceUpdate({});
  }, [location.pathname, location.key]); // Also track location.key to catch all navigation changes

  // Handle breadcrumb click
  const handleBreadcrumbClick = (href: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault(); // Prevent default Link behavior
    }
    
    const isCurrentPage = location.pathname === href;
    const isHomePage = href === '/dashboard';
    
    isNavigatingViaBreadcrumbRef.current = true;
    
    // If clicking on home icon, clear the entire history
    if (isHomePage) {
      navigationHistory = [];
      
      // If already on dashboard, trigger refresh
      if (isCurrentPage) {
        console.log('🔄 Attempting to refresh Dashboard');
        triggerRefresh(href);
        navigate(href, { 
          replace: false, 
          state: { refresh: true, timestamp: Date.now() } 
        });
      } else {
        // Navigate to dashboard
        navigate(href);
      }
      return;
    }
    
    // Find the actual index in the full history
    const clickedItem = navigationHistory.find(item => item.href === href);
    if (clickedItem) {
      const actualIndex = navigationHistory.indexOf(clickedItem);
      // Trim history to the clicked item
      navigationHistory = navigationHistory.slice(0, actualIndex + 1);
    }
    
    // If clicking on the current page, trigger refresh if handler exists
    if (isCurrentPage) {
      console.log('🔄 Attempting to refresh current page:', href);
      // Try to trigger the registered refresh handler
      triggerRefresh(href);
      // Also navigate with refresh state as fallback for pages without handlers
      navigate(href, { 
        replace: false, 
        state: { refresh: true, timestamp: Date.now() } 
      });
    } else {
      // Navigate to different page
      navigate(href);
    }
    
    // Force re-render to update breadcrumb display
    forceUpdate({});
  };

  // Handle ellipsis click (go to the item before the last 2 visible items)
  // If showing: Home > ... > D > E, clicking ... should go to C
  const handleEllipsisClick = () => {
    const historyWithoutDashboard = navigationHistory.filter(item => item.href !== '/dashboard');
    if (historyWithoutDashboard.length > 3) {
      // We're showing the last 2 items, so go to the 3rd from last (C in the example)
      const targetIndex = historyWithoutDashboard.length - 3;
      const targetItem = historyWithoutDashboard[targetIndex];
      if (targetItem) {
        handleBreadcrumbClick(targetItem.href);
      }
    }
  };

  // Build breadcrumb items to display
  const buildBreadcrumbItems = () => {
    const items: Array<BreadcrumbItem & { isEllipsis?: boolean; isHome?: boolean; isLast?: boolean }> = [];
    
    const currentPath = location.pathname;
    
    // Always show Home icon first (clickable to go to Dashboard)
    items.push({ label: '', href: '/dashboard', isHome: true });
    
    // If we're on dashboard, show the trail from where we came from with "Home" text at the END
    if (currentPath === '/dashboard') {
      // Get the history excluding the current dashboard visit
      const historyWithoutDashboard = navigationHistory.filter(item => item.href !== '/dashboard');
      
      // If there's history (user navigated from somewhere), show the trail
      if (historyWithoutDashboard.length > 0) {
        // Apply ellipsis logic if history is too long (more than 3 items)
        if (historyWithoutDashboard.length > 3) {
          items.push({ label: '...', href: '', isEllipsis: true });
          
          // Add last 2 items from history
          const lastTwo = historyWithoutDashboard.slice(-2);
          lastTwo.forEach((item) => {
            items.push({
              ...item,
              isLast: false
            });
          });
        } else {
          // Show all pages in the trail
          historyWithoutDashboard.forEach((item) => {
            items.push({
              ...item,
              isLast: false
            });
          });
        }
      }
      
      // Add "Home" text at the END
      items.push({ 
        label: 'Home', 
        href: '/dashboard',
        isLast: true 
      });
      
      return items;
    }
    
    // Get the history excluding dashboard
    const historyWithoutDashboard = navigationHistory.filter(item => item.href !== '/dashboard');
    
    // If history has more than 3 items (excluding dashboard), show ellipsis
    // Show: Home > ... > [last 2 items] (D > E in your example)
    if (historyWithoutDashboard.length > 3) {
      items.push({ label: '...', href: '', isEllipsis: true });
      
      // Add last 2 items from history
      const lastTwo = historyWithoutDashboard.slice(-2);
      lastTwo.forEach((item, idx) => {
        items.push({
          ...item,
          isLast: idx === lastTwo.length - 1
        });
      });
    } else {
      // Show all history items
      historyWithoutDashboard.forEach((item, idx) => {
        items.push({
          ...item,
          isLast: idx === historyWithoutDashboard.length - 1
        });
      });
    }
    
    return items;
  };

  const breadcrumbItems = buildBreadcrumbItems();

  return (
    <nav aria-label="Breadcrumb" className="flex items-center space-x-1 text-sm text-muted-foreground">
      {breadcrumbItems.map((item, index) => {
        const isLast = item.isLast;
        const isHome = item.isHome;
        const isEllipsis = item.isEllipsis;
        
        return (
          <div key={`${item.href}-${index}`} className="flex items-center">
            {index > 0 && <ChevronRight className="h-4 w-4 mx-1 flex-shrink-0" />}
            
            {isEllipsis ? (
              <button
                onClick={handleEllipsisClick}
                className="hover:text-foreground transition-colors p-0 bg-transparent border-none cursor-pointer"
                title="Go back one more step"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            ) : (
              <Link
                to={item.href}
                onClick={(e) => handleBreadcrumbClick(item.href, e)}
                className={cn(
                  'hover:text-foreground transition-colors cursor-pointer flex items-center',
                  isLast && 'font-medium text-foreground',
                  !isHome && !item.label && 'truncate max-w-[150px] sm:max-w-[200px] md:max-w-none'
                )}
                title={isHome && !item.label ? 'Home' : item.label}
              >
                {isHome && !item.label ? (
                  <Home className="h-4 w-4" />
                ) : (
                  item.label
                )}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}
